import type { CSSProperties, ReactNode } from 'react'

import { Icon } from '../primitives/Icon'

export type LeaveCheckStatus = 'ok' | 'attn'

export interface LeaveCheckItem {
  /** Stable key + testid suffix (e.g. "balance", "coverage"). */
  key: string
  status: LeaveCheckStatus
  /** One-line fact; ReactNode so numbers can be emphasised inline. */
  fact: ReactNode
}

interface LeaveCheckProps {
  items: ReadonlyArray<LeaveCheckItem>
  /** Form isn't ready yet (no dates) → show the muted empty state instead. */
  ready: boolean
  /** Date-independent rows that render at the top regardless of `ready` (e.g.
   *  the unpaid heads-up — it's about the leave-type choice, not the dates, so
   *  it must show before a date is picked). */
  persistentItems?: ReadonlyArray<LeaveCheckItem>
  /** Header title. Defaults to the apply-page wording. The leave-detail view
   *  reuses this panel to state the same facts on an already-filed leave, so
   *  it overrides this to "At a glance". */
  title?: string
  /** Footnote shown when any △ row is present. Defaults to the apply-page
   *  wording ("…You can still submit."), which doesn't fit a filed leave —
   *  the detail view overrides it. */
  footnote?: ReactNode
  /** Hide the "title / advisory" header row. The leave-detail view renders
   *  this under a "Good to know" tab, so the header would just be redundant. */
  hideHeader?: boolean
  /** Drop the card chrome (border / background / shadow / padding). Used when
   *  the panel already sits inside a bordered container (the leave-detail
   *  modal) so it doesn't read as a box-in-a-box. */
  bare?: boolean
  /** When set, the request is HARD-blocked (e.g. blackout_blocked,
   *  unpaid_not_allowed). Capacity is no longer a block (it warns and is decided at approval). The panel then shows ONLY this block
   *  reason — the advisory rows, empty state, and "△ = worth a look" footnote
   *  are all suppressed, because none of that matters once the request can't be
   *  filed. The "advisory" header label is dropped too. */
  blockMessage?: string
}

const CARD_STYLE: CSSProperties = {
  background: 'var(--surface)',
  border: '1px solid var(--border)',
  borderRadius: 12,
  boxShadow: 'var(--shadow-sm)',
  padding: '14px 16px',
}

const HEAD_STYLE: CSSProperties = {
  display: 'flex',
  alignItems: 'baseline',
  justifyContent: 'space-between',
  gap: 8,
  marginBottom: 10,
  paddingBottom: 10,
  borderBottom: '1px solid var(--border)',
}

/**
 * A check-circle (ok) or a warning triangle (attn), coloured by status.
 * Line icons rather than the app's filled material symbols so the row reads
 * as a quiet password-style checklist mark, not an alarm.
 */
function StatusGlyph({ status }: { status: LeaveCheckStatus }): JSX.Element {
  const color = status === 'ok' ? 'var(--success)' : 'var(--warning)'
  return (
    <svg
      viewBox="0 0 20 20"
      width={18}
      height={18}
      fill="none"
      stroke={color}
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      style={{ marginTop: 1, flex: 'none' }}
    >
      {status === 'ok' ? (
        <>
          <circle cx="10" cy="10" r="8" />
          <path d="M6.5 10.2l2.3 2.3 4.7-4.9" />
        </>
      ) : (
        <>
          <path d="M10 2.5l7.5 13.5H2.5z" />
          <path d="M10 8v3.5M10 14h.01" />
        </>
      )}
    </svg>
  )
}

/**
 * "Things to know" — a compact, advisory checklist that replaces the
 * dark ILMU Leave Assistant panel in the apply-leave rail. Each row is led
 * by a ✓ (ok) or △ (heads up), password-requirements style: the glyph in the
 * left margin is the whole signal, so the column scans at a glance. Nothing
 * here blocks Submit; "who decides" (escalation) lives in the Approver path
 * panel above, not on these fact rows.
 */
/** One checklist row: △/✓ glyph in the left margin + the fact. */
function CheckRow({ item }: { item: LeaveCheckItem }): JSX.Element {
  return (
    <li
      data-testid={`leave-check-${item.key}`}
      data-status={item.status}
      style={{
        display: 'grid',
        gridTemplateColumns: '18px 1fr',
        gap: 9,
        alignItems: 'start',
        padding: '7px 0',
        fontSize: 13,
        color: item.status === 'ok' ? 'var(--text-2)' : 'var(--text)',
      }}
    >
      <StatusGlyph status={item.status} />
      <span style={{ lineHeight: 1.45 }}>{item.fact}</span>
    </li>
  )
}

const LIST_STYLE: CSSProperties = {
  listStyle: 'none',
  margin: 0,
  padding: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
}

export function LeaveCheck({
  items,
  ready,
  persistentItems,
  title = 'Things to know',
  footnote = '△ = worth a look, not a blocker. You can still submit.',
  hideHeader = false,
  bare = false,
  blockMessage,
}: LeaveCheckProps): JSX.Element {
  const persistent = persistentItems ?? []
  const blocked = blockMessage != null
  // △ footnote applies if any rendered row is a heads-up — including the
  // persistent ones, which show even before dates are picked.
  const hasAttn =
    persistent.some((i) => i.status === 'attn') ||
    (ready && items.some((i) => i.status === 'attn'))
  return (
    <section
      data-testid="leave-check"
      style={bare ? { background: 'transparent', padding: 0 } : CARD_STYLE}
    >
      {!hideHeader ? (
        <header style={HEAD_STYLE}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <Icon
              name="emoji_objects"
              size={16}
              color="#FFBF00"
              aria-hidden="true"
            />
            <span style={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.02em', color: 'var(--text-2)' }}>
              {title}
            </span>
          </span>
        </header>
      ) : null}

      {blocked ? (
        <div
          data-testid="leave-check-block"
          role="alert"
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 9,
            fontSize: 13,
            lineHeight: 1.45,
            color: 'var(--danger-fg)',
          }}
        >
          <span aria-hidden style={{ flexShrink: 0, marginTop: 1 }}>
            ⛔
          </span>
          <span>{blockMessage}</span>
        </div>
      ) : (
        <>

      {persistent.length > 0 ? (
        <ul style={LIST_STYLE}>
          {persistent.map((item) => (
            <CheckRow key={item.key} item={item} />
          ))}
        </ul>
      ) : null}

      {!ready ? (
        <p
          style={{
            margin: persistent.length > 0 ? '8px 0 0' : 0,
            fontSize: 12.5,
            color: 'var(--text-3)',
          }}
        >
          Pick your dates to run the {persistent.length > 0 ? 'remaining ' : ''}check.
        </p>
      ) : (
        <>
          <ul style={LIST_STYLE}>
            {items.map((item) => (
              <CheckRow key={item.key} item={item} />
            ))}
          </ul>
          {hasAttn ? (
            <div style={{ marginTop: 10, fontSize: 11, color: 'var(--text-3)' }}>
              {footnote}
            </div>
          ) : null}
        </>
      )}
        </>
      )}
    </section>
  )
}
